/**
 * Ties the bundle, the derived rollup and the filter bar together, and owns the
 * drill-through so any page can open one without threading state through props.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import { useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import { useLoadedDataset } from './DatasetProvider'
import { usePageLimit } from '@/access/pageLimit'
import { derive, trailingScores, type Derived, type TrailingScores } from './selectors'
import { trailingWindow, TRAILING_MONTHS } from '@/measures/trailing'
import { volumeFlags, type VolumeFlags } from '@/measures/claimVolume'
import { applyFilters, lastClosedMonth, monthRangeOf, useFilters, type Scope } from './filters'
import { optionsOf } from './filterStorage'
import { coverageOf, type Coverage } from './coverage'
import { claimFilterNote, claimSplitOf } from './claimCells'
import { CollectionWindowContext } from './CollectionWindowContext'
import { DrillThrough } from '@/components/DrillThrough'
import { useUrlState, maybeText } from '@/ui/urlState'
import { useWeights, type WeightsState } from './weights'
import { basisDefaultFor, basisLabel, type ClaimBasis } from './basisLabel'
import { weightCrosstab } from './weightedCrosstab'
import type { CrosstabDict } from './types'

interface AppDataCtx {
  derived: Derived
  scope: Scope
  openMonths: string[]
  /** Of `openMonths`, the ended months held open while their payroll loads. */
  payrollPending: string[]
  /** Where the range ends when none is picked: the last closed month, or null. */
  closedThrough: string | null
  /**
   * Everybody's trailing score: the last TRAILING_MONTHS closed months ending at the
   * range's end. Not filtered, like `derived`. See measures/trailing.ts.
   */
  trailing: TrailingScores
  /**
   * The build's claim-volume warning by person and month (review #374; see
   * measures/claimVolume.ts): months billing far above the job title, usually claims
   * the matcher handed one person. Marked ⚑; no figure changes.
   */
  volume: VolumeFlags
  /** Which grains this bundle actually carries. See data/coverage.ts. */
  coverage: Coverage
  /** Opens the drill-through. `back` adds a labelled return link. */
  openPerson: (name: string, back?: { label: string; onBack: () => void }) => void
  /**
   * What the claim count is: 'weighted' only while the switch is on AND the weights
   * loaded; otherwise 'standard'. See data/weights.ts and components/ClaimBasisToggle.
   */
  claimBasis: ClaimBasis
  setClaimBasis: (b: ClaimBasis) => void
  weights: WeightsState
  /** A switched figure's label for the current basis. See `basisLabel`. */
  label: (s: string) => string
  /**
   * The crosstab the breakdowns read: weighted cells while the switch is on
   * (data/weightedCrosstab.ts), the bundle's own otherwise. Read this, not
   * `dataset.crosstab`, for any claim count by state, payer type or facility.
   */
  crosstab: CrosstabDict
  /**
   * What a claim type, service type or status filter would model on this bundle, if
   * one were set: the hover on those controls' M. Null when they are exact — the
   * bundle carries the claim cells and the Claims switch is on Standard. See
   * data/claimCells.ts.
   */
  claimFilterNote: string | null
}

const Ctx = createContext<AppDataCtx | null>(null)

export function useAppData(): AppDataCtx {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAppData must be used inside <AppDataProvider>')
  return ctx
}

export function AppDataProvider({ children }: { children: ReactNode }) {
  const dataset = useLoadedDataset()
  const { filters, prune } = useFilters()

  // The expensive pass. Runs once per bundle and month range, not per filter change:
  // the range changes every person's figures, so it has to be applied here and not in
  // `applyFilters` — otherwise it only reaches the pages that read `scope.months`.
  // A month still running is left out unless picked; see lastClosedMonth.
  const closedThrough = useMemo(() => {
    const months = [...new Set(dataset.touchMonths.map((r) => r.month))].sort()
    return lastClosedMonth(months, dataset.openMonths)
  }, [dataset])
  const { from, to } = monthRangeOf(filters, closedThrough)
  const weights = useWeights()
  // Each page opens on its own default: Weighted on the Scorecard, Standard elsewhere.
  const { pathname } = useLocation()
  const [wanted, setWanted] = useState<ClaimBasis>(() => basisDefaultFor(pathname))
  useEffect(() => setWanted(basisDefaultFor(pathname)), [pathname])
  const setClaimBasis = useCallback((b: ClaimBasis) => setWanted(b), [])
  const adjustments = wanted === 'weighted' && weights.status === 'ok' ? weights.adjustments : null
  const claimBasis: ClaimBasis = adjustments ? 'weighted' : 'standard'
  const derived = useMemo(() => derive(dataset, { from, to }, adjustments), [dataset, from, to, adjustments])

  // The trailing score's window ends where the range ends (measures/trailing.ts). Keyed
  // on the window itself, so picking a new START month does not re-run the second derive.
  const rangeEnd = derived.months[derived.months.length - 1] ?? null
  const trailingKey = useMemo(
    () => trailingWindow(derived.allMonths, dataset.openMonths, rangeEnd).join(','),
    [derived.allMonths, dataset.openMonths, rangeEnd],
  )
  // `derived` is read only to skip a second derive when the window is the range. Leaving
  // it out of the deps is deliberate: the result does not change with it. The Weighted
  // claims switch reaches the trailing score too, as it does every other score.
  const trailing = useMemo(
    () =>
      trailingScores(dataset, trailingKey ? trailingKey.split(',') : [], TRAILING_MONTHS, derived, adjustments),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataset, trailingKey, adjustments],
  )
  const crosstab = useMemo(
    () => (adjustments && weights.status === 'ok' ? weightCrosstab(dataset.crosstab, weights.cells) : dataset.crosstab),
    [adjustments, weights, dataset.crosstab],
  )
  const coverage = useMemo(() => coverageOf(dataset), [dataset])
  const volume = useMemo(() => volumeFlags(dataset.claimVolumeOutliers), [dataset.claimVolumeOutliers])

  // A filter remembered from last visit may name something this bundle does not
  // have. Drop it before paint, so a vanished employee never shows as "0 people".
  useLayoutEffect(() => prune(optionsOf(derived)), [derived, prune])

  // The claim filters' cells, parsed once per bundle. Null on a bundle without them,
  // which keeps the modelled filters (data/claimCells.ts).
  const split = useMemo(() => claimSplitOf(dataset), [dataset])
  const scope = useMemo(
    () => applyFilters(derived, dataset.composition, filters, split, crosstab),
    [derived, dataset.composition, filters, split, crosstab],
  )

  /*
   * The open drill-through, by name, kept in this history entry — not the URL, since
   * it names a person (see ui/urlState.ts). So "← Back" from its "Claims" or "Touches"
   * buttons reopens it over the page it was on. Leaving the page closes it without any
   * code here: the next page's entry has no `person`, as the Ledger's drawer closes
   * when those buttons switch tab.
   *
   * `back` — a labelled "return to the team" link — is a callback, so it cannot be kept
   * in history. It holds for the opening that set it and is simply absent after a reload.
   */
  const [drillName, setDrillName] = useUrlState('person', null, maybeText, {
    personal: true,
  })
  const [back, setBack] = useState<{ label: string; onBack: () => void } | undefined>()

  // A page-limited reader has no person page, so no drill-through either: a search
  // hit or a link in history cannot open one over the pages they do have.
  const limited = usePageLimit() !== null
  const openPerson = useCallback(
    (name: string, back?: { label: string; onBack: () => void }) => {
      if (limited || !derived.byName.has(name)) return
      setBack(() => back)
      setDrillName(name)
    },
    [derived, setDrillName, limited],
  )
  const closePerson = () => {
    setBack(undefined)
    setDrillName(null)
  }

  // Looked up in the scope, reweighted by the claim filter, so the drill-through shows
  // the same figures and the same rank as the row it was opened from, and follows the
  // filter bar while it is open.
  const drillPerson = drillName && !limited ? (scope.byName.get(drillName) ?? null) : null

  const value = useMemo<AppDataCtx>(
    () => ({
      derived,
      scope,
      openMonths: dataset.openMonths,
      payrollPending: dataset.payrollPending ?? [],
      closedThrough,
      trailing,
      volume,
      coverage,
      openPerson,
      claimBasis,
      setClaimBasis,
      weights,
      label: (s: string) => basisLabel(s, claimBasis),
      crosstab,
      claimFilterNote: claimFilterNote({
        filtering: true,
        exact: split !== null,
        weighted: claimBasis === 'weighted',
      }),
    }),
    [derived, scope, dataset.openMonths, dataset.payrollPending, closedThrough, trailing, volume, coverage, openPerson, claimBasis, setClaimBasis, weights, crosstab, split],
  )

  return (
    <Ctx.Provider value={value}>
      <CollectionWindowContext.Provider value={derived.collectionWindow}>
      {children}
      {drillPerson && (
        <DrillThrough
          person={drillPerson}
          derived={derived}
          openMonths={dataset.openMonths}
          onClose={closePerson}
          backLabel={back?.label}
          onBack={
            back
              ? () => {
                  const cb = back.onBack
                  closePerson()
                  cb()
                }
              : undefined
          }
        />
      )}
      </CollectionWindowContext.Provider>
    </Ctx.Provider>
  )
}
